import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { formatAdminError } from "@/lib/adminApi";

export default function AdminLogin() {
  const { signIn, isAuthed } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const target = location.state?.from || "/admin/overview";

  useEffect(() => {
    if (isAuthed) navigate(target, { replace: true });
  }, [isAuthed, navigate, target]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password) {
      setError("Enter both username and password.");
      return;
    }
    setSubmitting(true);
    try {
      await signIn(username.trim(), password);
      toast.success("Welcome back, Isaac.");
      navigate(target, { replace: true });
    } catch (err) {
      const msg = formatAdminError(err, "Sign in failed.");
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-950 text-white grid place-items-center px-5 relative overflow-hidden"
      style={{ fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" }}
      data-testid="admin-login-page"
    >
      {/* Decorative grid + glow */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(14, 165, 233, 0.18) 1px, transparent 1px), linear-gradient(to bottom, rgba(14, 165, 233, 0.18) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at center, rgba(0,0,0,0.7), transparent 70%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute -top-40 -right-40 h-[480px] w-[480px] rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(212,160,23,0.18), transparent)",
        }}
      />

      <div className="relative w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-slate-400 hover:text-white transition-colors mb-8"
          data-testid="admin-login-back"
        >
          <ArrowRight size={12} className="-rotate-180" /> Back to public site
        </Link>

        <div
          className="rounded-2xl border border-white/10 bg-slate-900/80 backdrop-blur p-8 sm:p-10 shadow-[0_24px_64px_-24px_rgba(0,0,0,0.6)]"
        >
          <div className="flex items-center gap-3 mb-7">
            <span
              className="inline-block h-10 w-10 rounded-full bg-[#D4A017] text-slate-900 grid place-items-center font-black text-sm"
              aria-hidden="true"
            >
              SS
            </span>
            <div>
              <p
                className="text-[10px] uppercase tracking-[0.24em] text-[#D4A017] font-bold"
                style={{ fontFamily: "Outfit, ui-sans-serif" }}
              >
                Silky Smooth
              </p>
              <p className="text-sm font-bold">Admin Console</p>
            </div>
          </div>

          <h1
            className="text-3xl sm:text-4xl font-extrabold leading-[1.05] tracking-tight text-white"
            style={{ fontFamily: "Outfit, ui-sans-serif" }}
          >
            Isaac, sign in.
          </h1>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            This console is for the owner only. Bookmark it — your session lasts
            30 days.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate>
            <Field
              label="Username"
              id="admin-username"
              testId="admin-login-username"
            >
              <input
                id="admin-username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="admin-input"
                data-testid="admin-input-username"
                disabled={submitting}
              />
            </Field>
            <Field
              label="Password"
              id="admin-password"
              testId="admin-login-password"
            >
              <input
                id="admin-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="admin-input"
                data-testid="admin-input-password"
                disabled={submitting}
              />
            </Field>

            {error && (
              <p
                role="alert"
                className="rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-200 text-sm px-4 py-3"
                data-testid="admin-login-error"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-sky-500 hover:bg-sky-400 active:scale-[0.99] text-slate-950 font-bold text-sm tracking-wide h-12 transition-all shadow-[0_8px_24px_-8px_rgba(14,165,233,0.6)] disabled:opacity-60"
              data-testid="admin-login-submit"
            >
              {submitting ? (
                <>
                  <span className="h-4 w-4 rounded-full border-2 border-slate-900/40 border-t-slate-900 animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  <Lock size={14} /> Sign In
                </>
              )}
            </button>
          </form>

          <div className="mt-7 pt-6 border-t border-white/10 flex items-start gap-3 text-[12px] text-slate-400 leading-relaxed">
            <ShieldCheck size={14} className="mt-0.5 text-[#D4A017]" />
            <p>
              Anti-abuse: failed attempts are logged. After 5 misses in 15 minutes
              you'll be temporarily locked out. Don't share these credentials.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        .admin-input {
          width: 100%;
          height: 3rem;
          border-radius: 0.75rem;
          border: 1px solid rgba(255,255,255,0.08);
          background: rgba(15, 23, 42, 0.65);
          padding: 0 1rem;
          font-size: 1rem;
          color: #fff;
          transition: border-color .2s, box-shadow .2s, background .2s;
        }
        .admin-input:focus {
          outline: none;
          border-color: #38BDF8;
          background: rgba(15, 23, 42, 0.9);
          box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.18);
        }
      `}</style>
    </div>
  );
}

function Field({ label, id, children, testId }) {
  return (
    <div data-testid={testId}>
      <label
        htmlFor={id}
        className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-300 mb-2"
        style={{ fontFamily: "Outfit, ui-sans-serif" }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}
